import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { getPageNumber } from '~/components/AnnotationsV2/shared/annotationUtils';
import { usePDFOutlineContext } from '~/components/AssetModalV2/Content/Footer/contexts/PDFOutlineContext';
import { QueryParamNames } from '~/constants/search';
import { centralizedClipIsPDFTypeSelector } from '~/store/centralizedClip/selectors';

/**
 * Hook that scrolls to the correct page when navigating to a PDF with a discussionId.
 * When a discussionId is in the URL, this finds the discussion and scrolls to its page.
 * Waits for both discussions to load and PDF to be ready (numPages > 0).
 */
export const usePDFDiscussionPageSync = (discussions: ClipDiscussion[] | undefined) => {
  const isPDF = useSelector(centralizedClipIsPDFTypeSelector);
  const { changePage, numPages } = usePDFOutlineContext();

  useEffect(() => {
    // Wait for PDF to be ready (numPages > 0 means PDF is loaded)
    const isPDFLoaded = numPages > 0;
    if (!isPDF || !discussions || !isPDFLoaded) {
      return;
    }

    const activeDiscussionId = new URLSearchParams(window.location.search).get(QueryParamNames.discussionId);
    if (!activeDiscussionId) {
      return;
    }

    const discussion = discussions.find((d) => d.id === activeDiscussionId);
    if (discussion?.annotations) {
      const pageNumber = getPageNumber(discussion.annotations);
      if (pageNumber !== undefined) {
        changePage(pageNumber);
      }
    }
  }, [isPDF, discussions, changePage, numPages]);
};
